import { useEffect, useRef, type ReactNode } from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";

import {
  NO_PLAN_YET,
  RECORD_NOT_FOUND,
  useCase,
  useCasePlan,
  useExtraction,
  type DemoRunResult,
} from "@/api";
import { Button } from "@/components/Button";
import { CaseGone } from "@/components/CaseGone";
import { EmptyState } from "@/components/EmptyState";
import { PageTitle } from "@/components/PageTitle";
import { chosenOutline, storedDraft } from "@/components/planAnswer";
import { StateMessage } from "@/components/StateMessage";
import { PLANNING_LEDE, PLANNING_TITLE } from "@/features/planning/head";
import { PlanningScreen } from "@/features/planning/PlanningScreen";
import { PlanningSkeleton } from "@/features/planning/PlanningSkeleton";
import { noStandWay, recordOf } from "@/lib/noStand";
import { CUTOUT_CLEARANCE_DEFAULT_MM } from "@/lib/placement";
import type { DemoOutline } from "@/lib/outline";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { usePlanning, type ConsolePreferences, type PlanningDraft } from "@/store/planning";

import styles from "./CaseRoute.module.css";

/**
 * Everything a stored version put on the board, as the board holds it.
 *
 * Two sources and not one, because the server keeps them apart: the pieces,
 * the bracket settings and the cutout settings are
 * `plan_version.planning_state`, the corners, the facade objects and the
 * holes are the document the ops log built (design §4.1). Neither is
 * derivable from the other. A stored state older than the cutout fields
 * (before 12.09.2026) answers their defaults — the same ones the server
 * fills in.
 */
function hydrationOf(plan: DemoRunResult): { draft: PlanningDraft; prefs: ConsolePreferences } {
  const state = plan.planningState ?? null;
  return {
    // The draft half is the print documents' reading too (`storedDraft`,
    // 12.10.2026) — one translation from the stored document to the board.
    draft: storedDraft(plan),
    prefs: {
      consoleSeries: state?.consoleSeries ?? "solid_1",
      consoleAdjustableFirst: state?.consoleAdjustableFirst ?? false,
      consoleOverrides: state?.consoleOverrides ?? {},
      cutoutClearanceMm: state?.cutoutClearanceMm ?? CUTOUT_CLEARANCE_DEFAULT_MM,
      cutoutRatePerMNet: state?.cutoutRatePerMNet ?? null,
      cutoutBaseNet: state?.cutoutBaseNet ?? null,
      cutoutSurcharges: state?.cutoutSurcharges ?? {},
      splitPriority: state?.splitPriority ?? "standard_first",
    },
  };
}

/**
 * The board's page for a Vorgang without a Stand (round-4 screen check,
 * R4-3): under the board's own head, like its sibling on the calculation —
 * title, lede and „noch kein Stand" (which Vorgang it is, the frame above
 * says) — so a screen reader lands on an `h1` here too, and the focus goes
 * there on arrival.
 */
function NoStandPage({ children }: { children: ReactNode }) {
  const heading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    heading.current?.focus();
  }, []);
  return (
    <div className={styles.page}>
      <header className={styles.head}>
        <PageTitle headingRef={heading} tabIndex={-1} className={styles.title}>
          {PLANNING_TITLE}
        </PageTitle>
        <p className={styles.lede}>{PLANNING_LEDE}</p>
        {/* Which Vorgang this is, the frame above the page says (design
            2026-10-22 §5) — the meta line keeps only what the frame does not. */}
        <p className={styles.meta}>noch kein Stand</p>
      </header>
      {children}
    </div>
  );
}

/**
 * `/vorgang/:caseId/planung` — the board for a Vorgang that already exists on
 * the server, not the register demo's own stateless `/planung` (design §9).
 *
 * Opening a case is a replay, not a fresh start (design §5): `useCasePlan`
 * asks `GET /cases/{id}/plan`, which walks the stored log (Stand 1, every
 * gesture since) and answers in the same shape `planManual` already does —
 * so the board underneath needs nothing new to read it. `useCase` supplies
 * the one figure the plan answer does not carry: `latestVersionNo`, the
 * optimistic lock every gesture from here on posts against.
 *
 * **The store is filled here, before the board is shown**, and that is the
 * point of the route rather than an implementation detail: until fix round 1
 * the plan answer was handed down as `initialResult` — specs, findings and
 * totals — while the board itself started empty, so reopening a Vorgang showed
 * a house with nothing on it and every element had to be laid again
 * (review, Critical: „F5 → alles da").
 */
export function CaseRoute() {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { data, isPending, error, dataUpdatedAt = 0 } = useCasePlan(caseId ?? null);
  const detail = useCase(caseId ?? null);
  // `useCasePlan` turns a 404 — no Stand 1 yet, the shape a case takes before
  // either entry path has written anything — into this one message. Computed
  // here, ahead of any early return, because it also decides whether the
  // extraction lookup below is worth making at all (Ruling T7-1): a Vorgang
  // that failed for any other reason has no reading to ask about.
  const noVersionYet = error instanceof Error && error.message === NO_PLAN_YET;
  // Not mine (any more): read off either answer, and **before** the data — a
  // Vorgang handed over while this board was open answers 404 on its next
  // read while the cache still holds the last plan beside the error (I-1).
  const gone = error?.message === RECORD_NOT_FOUND || detail.error?.message === RECORD_NOT_FOUND;
  // The tab names the board (GAP-03) — unless the Vorgang is gone, where
  // `CaseGone` names the tab itself; a child's effect runs before this one,
  // so naming the board here would overwrite it.
  useDocumentTitle(gone ? null : "Planung", detail.data?.caseNo);
  // A direct URL can land here for a Vorgang without a Stand — one whose
  // reading has succeeded but was never taken over (`TakeOver`, A-014), is
  // still running, found no shape, or never ran at all. Each has its own
  // cause and way on (`noStandWay`, R3-3).
  // Asked only where a drawing is on file — without one there is no reading
  // to ask about (Ruling T7-1, as on the record's head).
  const readable = !gone && noVersionYet && (detail.data?.documents.length ?? 0) > 0;
  const extraction = useExtraction(readable ? (caseId ?? null) : null);
  const hydrate = usePlanning((state) => state.hydrate);
  const clear = usePlanning((state) => state.clear);

  /**
   * One effect, and it runs again on every new plan answer — which is what
   * makes the 409 path work: `persist` invalidates `["case", id, "plan"]`, the
   * refetch lands as a new `data`, and the board is replaced with what the
   * server actually holds. The gesture that was rejected is gone from the
   * screen, which is the whole promise of an optimistic lock (design §9).
   *
   * `clear()` first, unconditionally: the store is a module singleton that
   * outlives the route, so a second Vorgang opened over the first would
   * otherwise inherit its elements. `hydrate` does that itself, and in one
   * `set` — two calls would let the board render once with the old plan under
   * the new Vorgang's outline.
   */
  /**
   * A new answer is not the only answer that hydrates (screen check
   * 15.10.2026, V-2): after a *refused* write the server holds what it held
   * before, the refetch is deep-equal to the cache, and the query keeps the
   * old object — `data` does not change, and the refused gesture used to stay
   * on the board. The board asks first (`hydrationAskedAt`), and the first
   * read that lands after that request puts the server's state back, same
   * object or not. A read from before the request does not count, and a read
   * that fails moves nothing (`dataUpdatedAt` stays): a board is never put
   * back to an answer older than a write it made.
   */
  const hydrationAskedAt = usePlanning((state) => state.hydrationAskedAt);
  const hydratedFrom = useRef<DemoRunResult | null>(null);
  useEffect(() => {
    if (!caseId || !data) return;
    const asked = hydrationAskedAt !== null && dataUpdatedAt >= hydrationAskedAt;
    if (hydratedFrom.current === data && !asked) return;
    hydratedFrom.current = data;
    const { draft, prefs } = hydrationOf(data);
    hydrate(draft, prefs);
  }, [caseId, data, dataUpdatedAt, hydrationAskedAt, hydrate]);

  /**
   * And the board is emptied on the way out.
   *
   * The store is a module singleton that outlives every route. Leaving a
   * Vorgang's plan in it meant the register demo's own `/planung` opened on
   * somebody's house — with `baseSteps` still at 1, so Rückgängig could not
   * even clear it (fix round 2, item 3). `PlanningRoute` clears on the way in
   * for the same reason from the other side; both, because either route may be
   * the first one mounted.
   */
  useEffect(() => clear, [clear]);

  if (!caseId) return <Navigate to="/" replace />;

  // The record's own words for it, the new owner named where known (R5-6).
  if (gone) return <CaseGone caseId={caseId} onRegister={() => navigate("/vorgaenge")} />;

  // A Vorgang without a Stand waits for its reading's answer too, so it does
  // not name one cause and then another. In the board's own geometry, under
  // the frame that already names the Vorgang — never a loading line (Task 10,
  // B-4).
  if (isPending || detail.isPending || (readable && extraction.isPending)) {
    return <PlanningSkeleton />;
  }

  if (noVersionYet) {
    // Round-3 screen check, R3-3: the cause that holds — a reading waiting
    // on „Übernehmen" (Ruling T7-1), one still running, one that found no
    // shape, or none at all — and the way on inside this Vorgang, not „Zum
    // Start".
    const way = noStandWay(caseId, extraction.data?.status ?? null, "planning");
    return (
      <NoStandPage>
        {/* Nothing to plan yet, and the one way on — the empty state of the
            board (UX gate round 4, #11), not a notice over an empty page. */}
        <EmptyState
          title={way.title}
          actions={
            <>
              <Button variant="primary" onClick={() => navigate(way.next.to)}>
                {way.next.label}
              </Button>
              <Button variant="quiet" onClick={() => navigate(recordOf(caseId))}>
                Zur Akte
              </Button>
            </>
          }
        >
          {way.text}
        </EmptyState>
      </NoStandPage>
    );
  }

  const failure = error ?? detail.error;
  if (failure) {
    return (
      <StateMessage title="Vorgang lässt sich nicht öffnen">
        {failure.message}
        <div>
          <Button variant="quiet" onClick={() => navigate("/")}>
            Zum Start
          </Button>
        </div>
      </StateMessage>
    );
  }

  const outline: DemoOutline | null = chosenOutline(data!);

  return (
    <PlanningScreen
      // A different Vorgang is a different board, not the same board with
      // other numbers: remounting drops the selection, the tool and every
      // pending write with it, so nothing of the last case can reach the next
      // one's version log.
      key={caseId}
      outline={outline}
      jobGratingType={data!.grating.type ?? null}
      caseId={caseId}
      // Never `null` here: the screen refuses to write without a number, and
      // silently not writing is the failure mode this route exists to close.
      // `detail.data` is guaranteed by the `isPending`/`error` gates above.
      versionNo={detail.data!.latestVersionNo}
      initialResult={data!}
    />
  );
}
